import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Box, Button, Typography } from "@mui/material";
import lodash, { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import editTestDataComponentStyles from "./EditTestDataComponent.module.css";
import EditTestDataField from "./EditTestDataField";
import EditTestDataObject from "./EditTestDataObject";

interface EditTestDataComponentProps {
  isExpandAll: boolean;
  expandCollapseKey: number;
  innerJson: any;
  handleInnerJsonChange: (changedJson: any) => void;
}

const EditTestDataComponent = ({
  isExpandAll,
  expandCollapseKey,
  innerJson,
  handleInnerJsonChange,
}: EditTestDataComponentProps) => {
  const [editJson, setEditJson] = useState(cloneDeep(innerJson));
  const isNullKeyExists = lodash.has(editJson, "");

  useEffect(() => {
    if (!lodash.isEqual(editJson, innerJson)) {
      setEditJson(cloneDeep(innerJson));
    }
  }, [innerJson]);

  const handleKeyChange = (oldKey: string, newKey: string) => {
    setEditJson((prev: any) => {
      // Helper function to recursively update the key
      const recursiveUpdate = (
        obj: any,
        oldKey: string,
        newKey: string
      ): any => {
        if (Array.isArray(obj)) {
          // If the value is an array, return it as is (do not rename keys in arrays)
          return obj.map((item) => {
            if (typeof item === "object" && item !== null) {
              return recursiveUpdate(item, oldKey, newKey); // Recursively process array objects
            }
            return item; // If it's a primitive value, no changes
          });
        } else if (typeof obj === "object" && obj !== null) {
          // If the value is an object, process each key
          const newObj: any = {};
          Object.entries(obj).forEach(([key, value]) => {
            if (key === oldKey) {
              newObj[newKey] = value; // Replace oldKey with newKey
            } else {
              newObj[key] = recursiveUpdate(value, oldKey, newKey); // Recursively process nested objects/arrays
            }
          });
          return newObj;
        }
        return obj; // If it's a primitive value (string, number, etc.), return it unchanged
      };

      // Process the entire JSON object recursively
      return recursiveUpdate(prev, oldKey, newKey);
    });
  };

  const handleOnRemove = (keyToRemove: string, objectIndex?: number) => {
    setEditJson((prev: any) => {
      // Helper function to recursively remove the key
      const recursiveRemove = (obj: any, isParent: boolean): any => {
        if (Array.isArray(obj)) {
          const newArray = cloneDeep(obj);
          if (objectIndex !== undefined) {
            newArray.splice(objectIndex, 1);
          }
          return newArray;
        } else if (typeof obj === "object" && obj !== null) {
          // If the value is an object, check each key
          const newObj: any = {};
          Object.entries(obj).forEach(([key, value]) => {
            if (key === keyToRemove && isParent) {
              // If we are at the parent level and the key matches, skip it
              return;
            } else {
              // If the key doesn't match, process it recursively
              newObj[key] = recursiveRemove(value, false);
            }
          });
          return newObj;
        }
        return obj; // If it's a primitive value (string, number, etc.), return it unchanged
      };

      // Recursively remove the key from the entire JSON structure, starting from the top level

      return recursiveRemove(prev, true);
    });
  };

  const handleOnAddAttribute = (
    isParentArray: boolean,
    index: number,
    objectIndex?: number
  ) => {
    setEditJson((prev: any) => {
      // Helper function to insert the key-value pair at the specified index level
      const insertAtIndex = (obj: any, currentDepth: number = 0): any => {
        if (Array.isArray(obj)) {
          const newArray = cloneDeep(obj);
          if (isParentArray && objectIndex === undefined) {
            newArray.splice(index + 1, 0, { "": "" });
            return newArray;
          } else if (!isParentArray && objectIndex !== undefined) {
            const updatedObject = { ...newArray[objectIndex], "": "" }; // Add new key-value pair
            newArray[objectIndex] = updatedObject; // Update the object in the array
            return newArray;
          }
        } else if (typeof obj === "object" && obj !== null) {
          // Handle when `objectIndex` is defined
          if (objectIndex !== undefined && currentDepth === 0) {
            const newObj: any = {};
            let position = 0;

            Object.entries(obj).forEach(([key, value]) => {
              if (position === objectIndex) {
                if (Array.isArray(value)) {
                  const newArray = cloneDeep(value);
                  newArray.splice(index, 0, { "": "" });
                  newObj[key] = newArray;
                  return newObj;
                } else if (typeof value === "object" && value !== null) {
                  // Target the specific object and modify its keys
                  const innerObj: any = {};
                  let innerPosition = 0;

                  Object.entries(value).forEach(([innerKey, innerValue]) => {
                    if (innerPosition === index) {
                      innerObj[""] = ""; // Add new key-value pair at the specified index
                    }
                    innerObj[innerKey] = innerValue; // Add existing key-value pairs
                    innerPosition++;
                  });

                  // If `index` exceeds the number of keys, append the new key-value pair
                  if (index >= innerPosition) {
                    innerObj[""] = "";
                  }

                  newObj[key] = innerObj; // Replace the modified object
                } else {
                  newObj[key] = value; // If not an object, leave it unchanged
                }
              } else {
                newObj[key] = value; // Add the rest of the keys unchanged
              }
              position++;
            });

            return newObj;
          } else {
            // Handle the normal case (no objectIndex specified)
            const newObj: any = {};
            let position = 0;

            Object.entries(obj).forEach(([key, value]) => {
              newObj[key] = value; // Add existing key-value pair
              if (position === index) {
                newObj[""] = ""; // Add new key-value only at the specified index
              }
              position++;
            });

            // If `index` exceeds the number of keys, append the new key-value pair
            if (index >= position) {
              newObj[""] = "";
            }

            return newObj;
          }
        }
        return obj; // Return unchanged primitive values
      };

      // Start modifying the JSON from the root
      return insertAtIndex(prev);
    });
  };

  const handleOnAddObject = (
    isParentArray: boolean,
    index: number,
    objectIndex?: number
  ) => {
    setEditJson((prev: any) => {
      // Helper function to insert a new object at the specified index level
      const insertObjectAtIndex = (obj: any, currentDepth: number = 0): any => {
        if (Array.isArray(obj)) {
          const newArray = cloneDeep(obj);
          if (isParentArray && objectIndex === undefined) {
            newArray.splice(index + 1, 0, { "": "" });
            return newArray;
          } else if (!isParentArray && objectIndex !== undefined) {
            const updatedObject = { ...newArray[objectIndex], "": {} }; // Add new key-value pair
            newArray[objectIndex] = updatedObject; // Update the object in the array
            return newArray;
          }
        } else if (typeof obj === "object" && obj !== null) {
          // Handle when `objectIndex` is defined
          if (objectIndex !== undefined && currentDepth === 0) {
            let position = 0;
            const newObj: any = {};
            Object.entries(obj).forEach(([key, value]) => {
              if (position === objectIndex) {
                if (Array.isArray(value)) {
                  const newArray = cloneDeep(value);
                  newArray.splice(index, 0, { "": "" });
                  newObj[key] = newArray;
                  return newObj;
                } else if (typeof value === "object" && value !== null) {
                  // Target the specific object and modify it
                  const innerObj: any = {};
                  let innerPosition = 0;
                  Object.entries(value).forEach(([innerKey, innerValue]) => {
                    if (innerPosition === index) {
                      innerObj[""] = {}; // Add new object at the specified index
                    }
                    innerObj[innerKey] = innerValue; // Add existing key-value pair
                    innerPosition++;
                  });

                  // If `index` exceeds the number of keys, append the new object
                  if (index >= innerPosition) {
                    innerObj[""] = {};
                  }
                  newObj[key] = innerObj; // Replace the modified object
                } else {
                  newObj[key] = value; // If not an object, leave it unchanged
                }
              } else {
                newObj[key] = value; // Add the rest of the keys unchanged
              }
              position++;
            });

            return newObj;
          } else {
            // Handle the normal case (no objectIndex specified)
            const newObj: any = {};
            let position = 0;
            Object.entries(obj).forEach(([key, value]) => {
              newObj[key] = value; // Add existing key-value pair
              if (position === index) {
                newObj[""] = {}; // Add new object only at the specified index
              }
              position++;
            });

            // If `index` exceeds the number of keys, append the new object
            if (index >= position) {
              newObj[""] = {};
            }
            return newObj;
          }
        }

        return obj; // Return unchanged primitive values
      };

      // Start modifying the JSON from the root
      return insertObjectAtIndex(prev);
    });
  };

  const handleOnArrayCheck = (oldKey: string, isArray: boolean) => {
    setEditJson((prev: any) => {
      const updatedJson = cloneDeep(prev);
      updatedJson[oldKey] = isArray ? [] : {};
      return updatedJson;
    });
  };

  useEffect(() => {
    handleInnerJsonChange(cloneDeep(editJson));
  }, [editJson]);

  const handleValueChange = (key: string, newValue: any) => {
    setEditJson((prev: any) => {
      const updatedJson = cloneDeep(prev);
      updatedJson[key] = newValue;
      return updatedJson;
    });
  };

  return (
    <>
      {Object.keys(innerJson).length > 0 ? (
        <Box>
          {Object.keys(editJson).map((key, index) => {
            const value = editJson[key];

            if (typeof value === "object" && value !== null) {
              return (
                <EditTestDataObject
                  key={index}
                  title={key}
                  data={editJson[key]}
                  objectIndex={index}
                  isExpandAll={isExpandAll}
                  expandCollapseKey={expandCollapseKey}
                  isInsideArray={Array.isArray(editJson)}
                  isNullKeyExists={isNullKeyExists}
                  handleTestDataObjectChange={(changedJson) => {
                    setEditJson((prev: any) => {
                      const updatedJson = cloneDeep(prev);
                      updatedJson[key] = cloneDeep(changedJson);
                      return cloneDeep(updatedJson);
                    });
                  }}
                  handleKeyChange={(newKey: string) => {
                    handleKeyChange(key, newKey);
                  }}
                  handleOnRemove={(
                    keyToRemove: string,
                    objectIndex?: number
                  ) => {
                    handleOnRemove(keyToRemove, objectIndex);
                  }}
                  handleOnAddAttribute={(
                    isParentArray: boolean,
                    fieldIndex: number,
                    objectIndex?: number
                  ) => {
                    handleOnAddAttribute(
                      isParentArray,
                      fieldIndex,
                      objectIndex
                    );
                  }}
                  handleOnAddObject={(
                    isParentArray: boolean,
                    fieldIndex: number,
                    objectIndex?: number
                  ) => {
                    handleOnAddObject(isParentArray, fieldIndex, objectIndex);
                  }}
                  handleOnArrayCheck={(oldKey: string, isArray: boolean) => {
                    handleOnArrayCheck(oldKey, isArray);
                  }}
                />
              );
            } else {
              return (
                <EditTestDataField
                  key={index}
                  fieldIndex={index}
                  keyContent={key}
                  value={value}
                  isInsideArray={Array.isArray(editJson)}
                  isNullKeyExists={isNullKeyExists}
                  handleKeyChange={(newKey: string) => {
                    handleKeyChange(key, newKey);
                  }}
                  handleValueChange={(newValue: string) => {
                    handleValueChange(key, newValue);
                  }}
                  handleOnRemove={(keyToRemove: string) => {
                    handleOnRemove(keyToRemove);
                  }}
                  handleOnAddAttribute={(
                    isParentArray: boolean,
                    fieldIndex: number
                  ) => {
                    handleOnAddAttribute(isParentArray, fieldIndex);
                  }}
                  handleOnAddObject={(
                    isParentArray: boolean,
                    fieldIndex: number
                  ) => {
                    handleOnAddObject(isParentArray, fieldIndex);
                  }}
                />
              );
            }
          })}
        </Box>
      ) : (
        <>
          <Box className={editTestDataComponentStyles.outterBox}>
            <Box className={editTestDataComponentStyles.boxShadow}>
              <Box className={editTestDataComponentStyles.contentBox}>
                <Typography>
                  Click
                  <span style={{ fontWeight: "bold" }}> Add Attribute </span>
                  to include a single key-value pair in your data. <br /> For
                  example, you can add <br />
                </Typography>
                <Box
                  component="pre"
                  className={editTestDataComponentStyles.codeContent}
                >
                  name: John
                </Box>
                <br />
                <Typography
                  component="span"
                  className={editTestDataComponentStyles.orBox}
                >
                  or
                </Typography>
                <Box
                  component="pre"
                  className={editTestDataComponentStyles.codeContent}
                >
                  age:30
                </Box>
                <br />
                <Typography>
                  This is useful for defining basic properties within your data.
                </Typography>
              </Box>
              <Box sx={{ padding: "1rem" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PlaylistAddIcon />}
                  onClick={() => {
                    handleOnAddAttribute(Array.isArray(editJson), 0);
                  }}
                >
                  Add Attribute
                </Button>
              </Box>
            </Box>
            <Box className={editTestDataComponentStyles.boxShadow}>
              <Box className={editTestDataComponentStyles.contentBox}>
                <Typography>
                  Click <span style={{ fontWeight: "bold" }}> Add Object</span>{" "}
                  to insert a nested object or array into your data.
                  <br /> For example, you can add <br />{" "}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: "8px",
                  }}
                >
                  <Box
                    component="pre"
                    className={editTestDataComponentStyles.codeContent}
                  >
                    {`address: {
  city: 'New York',
  zip: '10001'
}`}
                  </Box>
                  <Typography
                    component="span"
                    className={editTestDataComponentStyles.orBox}
                  >
                    or
                  </Typography>
                  <Box
                    component="pre"
                    className={editTestDataComponentStyles.codeContent}
                  >
                    {`addressList: [
  { city: 'New York', zip: '10001' },
  { city: 'Los Angeles', zip: '90001' }
]`}
                  </Box>
                </Box>
                <Typography>
                  This helps in organizing more complex, hierarchical data
                  structures.
                </Typography>
              </Box>
              <Box sx={{ padding: "1rem" }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PostAddIcon />}
                  onClick={() => handleOnAddObject(Array.isArray(editJson), 0)}
                >
                  Add Object
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default EditTestDataComponent;
